"use client";

import { useQueryState } from "nuqs";
import DropdownSearchBar from "./dropdown-search-bar";
import useSWR from "swr";
import { useRouter } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import { useDebounceValue } from "usehooks-ts";
import { builder, BuilderContent } from "@builder.io/sdk";
import { getBlogPostByTitleSlugTags } from "@/lib/builder-utils";
import { withUrl } from "@/lib/utils";

builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY!);

type SearchItem = {
  title: string;
  href: string;
};

const DropdownSearchWrapper: React.FC = () => {
  const [searchTerm, setSearchTerm] = useQueryState("search", {
    history: "push",
    defaultValue: "",
    clearOnDefault: true,
    shallow: false,
  });
  const [type, _] = useQueryState("type", {
    history: "push",
    defaultValue: "",
    clearOnDefault: true,
    shallow: false,
  });
  const [page, setPage] = useQueryState("page", {
    history: "push",
    defaultValue: "",
    clearOnDefault: true,
    shallow: false,
  });
  const [category, setCategory] = useQueryState("category", {
    history: "push",
    defaultValue: "",
    clearOnDefault: true,
    shallow: false,
  });
  const [sort, setSort] = useQueryState("sort", {
    history: "push",
    defaultValue: "",
    clearOnDefault: true,
    shallow: false,
  });

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm ?? "");
  const [debouncedSearchTerm] = useDebounceValue(localSearchTerm, 300);
  const router = useRouter();
  const searchBarRef = useRef<HTMLDivElement>(null);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setLocalSearchTerm(term);
    if (page) {
      setPage("");
    }
    if (category) {
      setCategory("");
    }
    if (sort) {
      setSort("");
    }
  };

  const handleSearchTermChange = (term: string) => {
    setLocalSearchTerm(term);
  };

  const handleResultClick = (result: SearchItem) => {
    router.push(result.href);
    setLocalSearchTerm("");
  };

  const fetcher = async (searchTerm: string, type: string) => {
    return await getBlogPostByTitleSlugTags(
      builder,
      searchTerm,
      type === "families" ? "Families" : "Professionals"
    );
  };

  const { data, isLoading } = useSWR(
    debouncedSearchTerm ? [debouncedSearchTerm, type] : null,
    ([searchTerm, type]) => fetcher(searchTerm, type),
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  useEffect(() => {
    if (searchBarRef.current) {
      searchBarRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [localSearchTerm]);

  return (
    <div ref={searchBarRef}>
      <DropdownSearchBar<SearchItem>
        initialSearchTerm={localSearchTerm}
        items={
          isLoading
            ? []
            : (data?.map((blog: BuilderContent) => ({
                title: blog.data?.blogPostTitle,
                href: withUrl(blog?.data?.url),
              })) ?? [])
        }
        placeholder="Search here"
        onSearch={handleSearch}
        onSearchTermChange={handleSearchTermChange}
        onResultClick={handleResultClick}
        renderResult={({ title }) => <div className="w-full">{title}</div>}
        fuseOptions={{
          keys: ["title", "summary", "content"],
          threshold: 1.0,
          // set should sort to true to run the sort function
          shouldSort: true,
          // do not sort as search result is already sorted from the backend
          sortFn: () => {
            return 0;
          },
        }}
      />
    </div>
  );
};

export default DropdownSearchWrapper;
